/**
 * The external-store primitive components read through `useSyncExternalStore`.
 *
 * Plan §2.2, restated as code: **WS frames never call `setState`.** They mutate a
 * per-channel buffer; one `requestAnimationFrame` flush drains those buffers into
 * these stores and bumps a version, and React re-renders once per frame however
 * many frames arrived. At the 131 f/s peak the idiomatic React path drops frames
 * on exactly the five seconds the user is watching — and a client that stops
 * draining its socket to keep up gets `SLOW_CONSUMER` and loses its book.
 *
 * Two consequences shape this file:
 *
 * 1. **The snapshot is a version number, not the data.** `useSyncExternalStore`
 *    requires `getSnapshot()` to return a value that is `Object.is`-stable while
 *    nothing has changed. Returning the state object would force every store to
 *    allocate a new one per update — for the orderbook ladder, sixty times a
 *    second, which is the reconciliation plan §2.3 bans. A monotonic integer is
 *    stable, allocation-free, and exactly as correct.
 * 2. **`notify()` is called by the flush, never by the socket.** It is public for
 *    that reason and for no other.
 *
 * No React import: `stores/` stays testable without a DOM, and a store that
 * imported React would be one `useEffect` away from becoming a component.
 */

export type Unsubscribe = () => void

export class ExternalStore<T> {
  #state: T
  #version = 0
  #dirty = false
  readonly #listeners = new Set<() => void>()

  constructor(initial: T) {
    this.#state = initial
  }

  /** The live state. Read directly by renderers; mutated only by the flush. */
  get state(): T {
    return this.#state
  }

  get version(): number {
    return this.#version
  }

  /** Changed since the last `notify()`. The flush uses it to skip stores that
   *  received nothing this frame. */
  get dirty(): boolean {
    return this.#dirty
  }

  get listenerCount(): number {
    return this.#listeners.size
  }

  /** `useSyncExternalStore(store.subscribe, store.getSnapshot)`. Bound, because
   *  React compares the function identity across renders. */
  readonly subscribe = (listener: () => void): Unsubscribe => {
    this.#listeners.add(listener)
    return () => {
      this.#listeners.delete(listener)
    }
  }

  readonly getSnapshot = (): number => this.#version

  /** Replace the state wholesale. Marks dirty; does **not** notify. */
  set(next: T): void {
    this.#state = next
    this.#dirty = true
  }

  /** Mutate in place — the ladder's path. Marks dirty; does **not** notify. */
  mutate(fn: (state: T) => void): void {
    fn(this.#state)
    this.#dirty = true
  }

  /** Mark dirty without touching the state, for a store whose data lives in a
   *  structure the caller mutated directly. */
  touch(): void {
    this.#dirty = true
  }

  /**
   * Bump the version and tell every subscriber, once.
   *
   * Called from the rAF flush. Calling it per frame would defeat the entire
   * buffering design, so it returns `false` when there was nothing to publish —
   * which makes an accidental per-frame call visible in a test rather than
   * merely expensive.
   */
  notify(): boolean {
    if (!this.#dirty) return false
    this.#dirty = false
    this.#version += 1
    for (const listener of [...this.#listeners]) listener()
    return true
  }
}
